import { useTranslation } from 'react-i18next';

import { Box, Stack, TextField, Typography } from '@mui/material';

import type { CatalogLanguage } from '../../model/types';

export interface LocalizedFieldValue {
  locale: string;
  text: string;
}

interface LocalizedFieldsEditorProps {
  /** The tenant's configured catalog languages (`GET /catalog/languages`). */
  languages: CatalogLanguage[];
  values: LocalizedFieldValue[];
  onChange: (values: LocalizedFieldValue[]) => void;
  /** Field name shown next to each language, e.g. "Label" or "Name". */
  fieldLabel: string;
  disabled?: boolean;
}

/**
 * Per-catalog-language editor for one translated text field — an attribute/option label, a
 * product-family or measurement-family name, and similar. Renders one row per configured catalog
 * language (default language first), each a plain text field prefixed with its locale code.
 * Clearing a row omits that locale from `values` entirely on the next save (translations are
 * required non-empty strings server-side); every other locale already present in `values` is left
 * untouched, even when its language isn't currently rendered (e.g. deactivated).
 */
export function LocalizedFieldsEditor({
  languages,
  values,
  onChange,
  fieldLabel,
  disabled,
}: LocalizedFieldsEditorProps) {
  const { t } = useTranslation('catalog');

  const sortedLanguages = [...languages].sort((a, b) => {
    if (a.defaultLocale !== b.defaultLocale) return a.defaultLocale ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  const textFor = (locale: string) => values.find((v) => v.locale === locale)?.text ?? '';

  const setTextFor = (locale: string, text: string) => {
    const others = values.filter((v) => v.locale !== locale);
    onChange(text.trim() === '' ? others : [...others, { locale, text }]);
  };

  if (sortedLanguages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('localizedFieldsEditor.noLanguages')}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {sortedLanguages.map((language) => (
        <TextField
          key={language.locale}
          label={`${fieldLabel} — ${language.name}${language.defaultLocale ? ` (${t('localizedFieldsEditor.defaultBadge')})` : ''}`}
          value={textFor(language.locale)}
          onChange={(e) => setTextFor(language.locale, e.target.value)}
          disabled={disabled}
          fullWidth
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 12,
                    color: 'text.secondary',
                    mr: 1,
                    flexShrink: 0,
                  }}
                >
                  {language.locale}
                </Box>
              ),
            },
          }}
        />
      ))}
    </Stack>
  );
}
